import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Permission } from '../models/models';
import api from './api';

interface PermissionState {
  permissionData: Permission[];
  userPermissions: Permission[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: PermissionState = {
  permissionData: [],
  userPermissions: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
  loading: false,
  error: null,
  successMessage: null,
};

export const fetchPermissions = createAsyncThunk<
  { data: Permission[]; total: number; totalPages: number; currentPage: number },
  { module?: string; limit?: number; offset?: number },
  { rejectValue: string }
>('permission/fetchAll', async (params, thunkAPI) => {
  try {
    const response = await api.get('/permissions', { params });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement permissions.');
  }
});

export const fetchUserPermissions = createAsyncThunk<
  Permission[],
  number,
  { rejectValue: string }
>('permission/fetchUser', async (user_id, thunkAPI) => {
  try {
    const response = await api.get(`/permissions/user/${user_id}`);
    return response.data.data as Permission[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement overrides.');
  }
});

export const setUserPermission = createAsyncThunk<
  void,
  { user_id: number; permission_id: number; granted: boolean },
  { rejectValue: string }
>('permission/setUser', async ({ user_id, permission_id, granted }, thunkAPI) => {
  try {
    await api.post(`/permissions/user/${user_id}`, { permission_id, granted });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur mise à jour permission.');
  }
});

export const deleteUserPermission = createAsyncThunk<
  void,
  { user_id: number; permission_id: number },
  { rejectValue: string }
>('permission/deleteUser', async ({ user_id, permission_id }, thunkAPI) => {
  try {
    await api.delete(`/permissions/user/${user_id}`, { params: { permission_id } });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur suppression override.');
  }
});

const permissionSlice = createSlice({
  name: 'PermissionSlice',
  initialState,
  reducers: {
    clearPermissionData(state) {
      state.permissionData = [];
      state.userPermissions = [];
    },
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissionData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchPermissions.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Erreur'; })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => { state.userPermissions = action.payload; })
      .addCase(setUserPermission.fulfilled, (state) => { state.successMessage = 'Permission mise à jour.'; })
      .addCase(setUserPermission.rejected, (state, action) => { state.error = action.payload || 'Erreur'; })
      .addCase(deleteUserPermission.fulfilled, (state) => { state.successMessage = 'Override supprimé.'; })
      .addCase(deleteUserPermission.rejected, (state, action) => { state.error = action.payload || 'Erreur'; });
  },
});

export const { clearPermissionData, clearMessages: clearPermissionMessages } = permissionSlice.actions;
export default permissionSlice.reducer;
